/**
 * ICP Chain Fusion Integration
 * Handles chain-key tokens (ckBTC, ckETH, ckUSDT, ckUSDC)
 * Enables BTC <-> ckBTC, ETH <-> ckETH conversions via ICP's native bridge
 */

import { HttpAgent, Actor } from '@dfinity/agent';
import { Principal } from '@dfinity/principal';
import type { SwapQuote } from './liquidityProviders';

// Official ICP Chain-Key Canister IDs (Mainnet)
export const ICP_CANISTER_IDS = {
  ckBTC: {
    ledger: 'mxzaz-hqaaa-aaaar-qaada-cai',
    minter: 'mqygn-kiaaa-aaaar-qaadq-cai',
  },
  ckETH: {
    ledger: 'ss2fx-dyaaa-aaaar-qacoq-cai',
    minter: 'sv3dd-oaaaa-aaaar-qacoa-cai',
  },
  ckDOGE: {
    ledger: 'xevnm-gaaaa-aaaar-qafnq-cai', // ckDOGE ledger (from ldclabs/ck-doge)
    minter: 'xrc4s-siaaa-aaaar-qafoa-cai', // ckDOGE minter
  },
  // ckUSDT and ckUSDC - to be added when officially launched
};

// Chain-key token mapping
const CHAIN_KEY_TOKENS: Record<string, string> = {
  'BTC': 'ckBTC',
  'ETH': 'ckETH',
  'DOGE': 'ckDOGE',
  'USDT': 'ckUSDT',
  'USDC': 'ckUSDC',
};

// Reverse mapping
const NATIVE_TOKENS: Record<string, string> = {
  'ckBTC': 'BTC',
  'ckETH': 'ETH',
  'ckDOGE': 'DOGE',
  'ckUSDT': 'USDT',
  'ckUSDC': 'USDC',
};

/**
 * Check if a token is a chain-key token
 */
export function isChainKeyToken(tokenSymbol: string): boolean {
  const upper = tokenSymbol.toUpperCase();
  return upper.startsWith('CK') && NATIVE_TOKENS[upper] !== undefined;
}

/**
 * Get chain-key token for native token
 */
export function getChainKeyToken(nativeToken: string): string | null {
  return CHAIN_KEY_TOKENS[nativeToken.toUpperCase()] || null;
}

/**
 * Get native token for chain-key token
 */
export function getNativeToken(chainKeyToken: string): string | null {
  return NATIVE_TOKENS[chainKeyToken.toUpperCase()] || null;
}

/**
 * Initialize ICP agent
 * Uses anonymous identity for quote fetching (no wallet needed)
 */
let cachedAgent: HttpAgent | null = null;

async function getICPAgent(): Promise<HttpAgent> {
  if (cachedAgent) return cachedAgent;
  
  try {
    const agent = await HttpAgent.create({
      host: 'https://ic0.app',
    });
    
    // Fetch root key for certificate verification (only in development)
    // In production, the root key is hardcoded in the agent
    if (import.meta.env.DEV) {
      try {
        await agent.fetchRootKey();
      } catch (error) {
        console.warn('Failed to fetch root key (this is normal in production):', error);
      }
    }
    
    cachedAgent = agent;
    return agent;
  } catch (error) {
    console.error('Failed to create ICP agent:', error);
    throw new Error('ICP agent initialization failed');
  }
}

/**
 * Get estimated ckBTC minting fee
 * In production, this would query the minter canister
 */
async function getCkBTCMintingFee(): Promise<number> {
  // ckBTC minting fee is ~0.0000001 BTC (10 satoshis) for KYT check
  // Plus Bitcoin network fee (variable)
  // For estimates, use 0.00001 BTC (~$0.87 at $87k BTC)
  return 0.00001;
}

/**
 * Get estimated ckETH minting fee
 */
async function getCkETHMintingFee(): Promise<number> {
  // ckETH minting fee is very low (~0.000001 ETH)
  // For estimates, use 0.0001 ETH (~$0.28 at $2.8k ETH)
  return 0.0001;
}

/**
 * Get estimated ckDOGE minting fee
 */
async function getCkDOGEMintingFee(): Promise<number> {
  // ckDOGE minting fee is very low (similar to ckBTC)
  // For estimates, use 0.01 DOGE (~$0.001 at $0.13 DOGE)
  return 0.01;
}

/**
 * Fetch quote for ICP Chain Fusion swap
 * Supports: Native token <-> ck token conversions
 * Example: BTC -> ckBTC, ckBTC -> BTC, ckBTC -> ckETH
 */
export async function fetchICPChainFusionQuote(
  fromToken: string,
  toToken: string,
  amount: number
): Promise<SwapQuote> {
  const provider = {
    name: 'ICP Chain Fusion',
    fee: 0.001, // 0.1% ICP network fee (estimated)
  };
  
  try {
    const fromUpper = fromToken.toUpperCase();
    const toUpper = toToken.toUpperCase();
    
    // Scenario 1: Native -> Chain-key (e.g., BTC -> ckBTC)
    const isNativeToChainKey = CHAIN_KEY_TOKENS[fromUpper] === toUpper;
    
    // Scenario 2: Chain-key -> Native (e.g., ckBTC -> BTC)
    const isChainKeyToNative = NATIVE_TOKENS[fromUpper] === toUpper;
    
    // Scenario 3: Chain-key -> Chain-key (e.g., ckBTC -> ckETH)
    const isChainKeyToChainKey = isChainKeyToken(fromUpper) && isChainKeyToken(toUpper);
    
    if (!isNativeToChainKey && !isChainKeyToNative && !isChainKeyToChainKey) {
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: `ICP Chain Fusion only supports chain-key token conversions (e.g., BTC ↔ ckBTC, ckBTC ↔ ckETH)`,
      };
    }
    
    // For now, return estimated quotes based on 1:1 conversion
    // In production, this would query ICP canisters for actual rates
    
    if (isNativeToChainKey || isChainKeyToNative) {
      // Get minting/burning fees
      let mintingFee = 0;
      if (fromUpper === 'BTC' || toUpper === 'BTC') {
        mintingFee = await getCkBTCMintingFee();
      } else if (fromUpper === 'ETH' || toUpper === 'ETH') {
        mintingFee = await getCkETHMintingFee();
      } else if (fromUpper === 'DOGE' || toUpper === 'DOGE') {
        mintingFee = await getCkDOGEMintingFee();
      }
      
      // Direct conversion: BTC <-> ckBTC is 1:1 minus fees
      const totalFee = provider.fee + (mintingFee / amount); // Network + minting fees only
      const conversionRate = 1 - totalFee;
      const estimatedOutput = Math.max(0, amount * (1 - totalFee));
      
      return {
        provider: provider.name,
        rate: conversionRate,
        fee: totalFee,
        providerFee: totalFee, // ICP network + minting fees (backend collects 0.3% separately)
        estimatedOutput,
        available: estimatedOutput > 0,
        isCrossChain: true,
        kycRisk: 'low',
        kycMessage: 'ICP Chain Fusion is decentralized - no KYC required',
      };
    }
    
    if (isChainKeyToChainKey) {
      // Chain-key to chain-key: Would need to get rates from ICP DEXs
      // For now, return as unavailable (requires ICP DEX integration)
      return {
        provider: provider.name,
        rate: 0,
        fee: provider.fee,
        estimatedOutput: 0,
        available: false,
        error: 'Chain-key to chain-key swaps require ICP DEX integration (coming soon)',
      };
    }
    
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Invalid token pair for ICP Chain Fusion',
    };
    
  } catch (error: any) {
    console.error('ICP Chain Fusion quote error:', error);
    return {
      provider: provider.name,
      rate: 0,
      fee: provider.fee,
      estimatedOutput: 0,
      available: false,
      error: 'Failed to fetch ICP Chain Fusion quote',
    };
  }
}

/**
 * Get BTC deposit address for minting ckBTC
 * This queries the ckBTC minter to get a unique BTC address for the user
 */
async function getBTCDepositAddress(userPrincipal: string): Promise<string> {
  try {
    // In production, this would call the ckBTC minter canister's get_btc_address method
    // For now, return instructions
    return 'Call ckBTC minter canister get_btc_address method with your Principal ID';
  } catch (error) {
    console.error('Failed to get BTC deposit address:', error);
    throw new Error('Failed to get deposit address from ICP');
  }
}

/**
 * Get ETH deposit address for minting ckETH
 */
async function getETHDepositAddress(userPrincipal: string): Promise<string> {
  try {
    // In production, this would call the ckETH minter canister's get_eth_address method
    return 'Call ckETH minter canister get_eth_address method with your Principal ID';
  } catch (error) {
    console.error('Failed to get ETH deposit address:', error);
    throw new Error('Failed to get deposit address from ICP');
  }
}

/**
 * Execute ICP Chain Fusion swap
 * NOTE: This requires user's ICP wallet/identity for full automation
 * For now, provides detailed instructions for manual execution
 */
export async function executeICPChainFusionSwap(
  quote: SwapQuote,
  userICPPrincipal: string,
  fromToken: string,
  toToken: string,
  amount: number,
  destinationAddress?: string
): Promise<{ success: boolean; txId?: string; instructions?: string; error?: string }> {
  try {
    const fromUpper = fromToken.toUpperCase();
    const toUpper = toToken.toUpperCase();
    
    // Validate user principal
    if (!userICPPrincipal || userICPPrincipal === 'aaaaa-aa') {
      return {
        success: false,
        error: 'Valid ICP Principal ID required. Please connect your ICP wallet (Plug, Stoic, or NFID)',
      };
    }
    
    // Determine swap type
    const isNativeToChainKey = CHAIN_KEY_TOKENS[fromUpper] === toUpper;
    const isChainKeyToNative = NATIVE_TOKENS[fromUpper] === toUpper;
    
    if (isNativeToChainKey) {
      // Minting: BTC -> ckBTC or ETH -> ckETH
      const chainKeyToken = toUpper;
      const canisterIds = ICP_CANISTER_IDS[chainKeyToken as keyof typeof ICP_CANISTER_IDS];
      
      if (!canisterIds) {
        return {
          success: false,
          error: `${chainKeyToken} minter not yet available on mainnet`,
        };
      }
      
      // Get deposit address (requires canister call)
      let depositAddress: string;
      try {
        if (fromUpper === 'BTC') {
          depositAddress = await getBTCDepositAddress(userICPPrincipal);
        } else if (fromUpper === 'ETH') {
          depositAddress = await getETHDepositAddress(userICPPrincipal);
        } else {
          depositAddress = `Get deposit address from ${chainKeyToken} minter`;
        }
      } catch (error) {
        depositAddress = `Error getting deposit address: ${error}`;
      }
      
      const instructions = `
🔄 Minting ${toToken} via ICP Chain Fusion

Step 1: Get Your Deposit Address
   • Minter Canister: ${canisterIds.minter}
   • Your Principal: ${userICPPrincipal}
   • Deposit Address: ${depositAddress}

Step 2: Send ${fromToken}
   • Amount: ${amount} ${fromToken}
   • To Address: [Your unique deposit address from Step 1]
   • Network: ${fromToken} mainnet

Step 3: Wait for Confirmations
   • ${fromToken === 'BTC' ? '6 Bitcoin confirmations (~60 min)' : '12 Ethereum confirmations (~3 min)'}
   • ICP will automatically mint ${quote.estimatedOutput?.toFixed(8) || amount} ${toToken}

Step 4: Receive ${toToken}
   • ${toToken} will appear in your ICP wallet
   • Principal: ${userICPPrincipal}
   • Ledger Canister: ${canisterIds.ledger}

Track Status:
   • ICP Dashboard: https://dashboard.internetcomputer.org
   • Minter Canister: https://ic0.app/${canisterIds.minter}

Note: Full automatic execution requires ICP wallet integration (Plug/Stoic/NFID).
      `.trim();
      
      return {
        success: false, // Manual until wallet integrated
        instructions,
        error: 'Manual execution required - Copy instructions above',
      };
    }
    
    if (isChainKeyToNative) {
      // Burning: ckBTC -> BTC or ckETH -> ETH
      const chainKeyToken = fromUpper;
      const canisterIds = ICP_CANISTER_IDS[chainKeyToken as keyof typeof ICP_CANISTER_IDS];
      
      if (!canisterIds) {
        return {
          success: false,
          error: `${chainKeyToken} minter not yet available on mainnet`,
        };
      }
      
      if (!destinationAddress) {
        return {
          success: false,
          error: `${toToken} destination address required for burning ${fromToken}`,
        };
      }
      
      const instructions = `
🔥 Burning ${fromToken} to receive ${toToken}

Step 1: Approve Minter
   • Approve ${amount} ${fromToken} to minter canister
   • Minter: ${canisterIds.minter}
   • Use your ICP wallet (Plug/Stoic/NFID)

Step 2: Request Withdrawal
   • Call minter canister's retrieve_btc_with_approval (for ckBTC)
   • Or retrieve_eth_with_approval (for ckETH)
   • Destination: ${destinationAddress}
   • Amount: ${amount} ${fromToken}

Step 3: Wait for Processing
   • ICP processes the burn
   • Releases ${quote.estimatedOutput?.toFixed(8) || amount} ${toToken}
   • To: ${destinationAddress}

Step 4: Receive ${toToken}
   • ${toToken} sent to your address
   • Check on ${toToken === 'BTC' ? 'Bitcoin' : 'Ethereum'} explorer

Track Status:
   • ICP Dashboard: https://dashboard.internetcomputer.org
   • Minter Canister: https://ic0.app/${canisterIds.minter}

Note: Full automatic execution requires ICP wallet integration (Plug/Stoic/NFID).
      `.trim();
      
      return {
        success: false, // Manual until wallet integrated
        instructions,
        error: 'Manual execution required - Copy instructions above',
      };
    }
    
    return {
      success: false,
      error: 'Invalid swap type for ICP Chain Fusion',
    };
    
  } catch (error: any) {
    console.error('ICP Chain Fusion execution error:', error);
    return {
      success: false,
      error: `Execution failed: ${error.message}`,
    };
  }
}

/**
 * Check if ICP Chain Fusion is available for a token pair
 */
export function isICPChainFusionAvailable(fromToken: string, toToken: string): boolean {
  const fromUpper = fromToken.toUpperCase();
  const toUpper = toToken.toUpperCase();
  
  // Check if it's a supported conversion
  const isNativeToChainKey = CHAIN_KEY_TOKENS[fromUpper] === toUpper;
  const isChainKeyToNative = NATIVE_TOKENS[fromUpper] === toUpper;
  
  return isNativeToChainKey || isChainKeyToNative;
}

/**
 * Get ICP Chain Fusion pathway description
 */
export function getICPChainFusionPathway(fromToken: string, toToken: string): string {
  const fromUpper = fromToken.toUpperCase();
  const toUpper = toToken.toUpperCase();
  
  if (CHAIN_KEY_TOKENS[fromUpper] === toUpper) {
    return `${fromToken} → ${toToken} (ICP Chain Fusion - Minting)`;
  }
  
  if (NATIVE_TOKENS[fromUpper] === toUpper) {
    return `${fromToken} → ${toToken} (ICP Chain Fusion - Burning)`;
  }
  
  return `${fromToken} → ${toToken} (ICP Chain Fusion)`;
}

